import { isImmutable, fromJS } from 'immutable';
import isEqual from 'lodash/isEqual';

import {
  VALUE_BIND_TYPE,
} from 'services/coursewareItem/constants';
import { logNewrelicAddPageAction } from '../../cookies/NewRelic';

class StateProvider {
  constructor() {
    this.state = fromJS({});
    this.subscriptions = {};
  }

  cleanPluginSubscriptions = (coursewareId) => {
    if (this.subscriptions !== {}) {
      const paths = Object.keys(this.subscriptions);
      paths.forEach((path) => {
        const pathId = path.split(',')[0];
        if (pathId === coursewareId) {
          this.remove([path]);
          this.subscriptions[path].length = 0;
        } else {
          this.subscriptions[path] = this.subscriptions[path].filter((subscription) => subscription.coursewareId !== coursewareId);
        }
      });
    }
  }


  keys() {
    const [...keys] = this.state.keys(); // convert Iterable into Array
    return keys || [];
  }

  has(id) {
    return this.state.has(id);
  }

  get(id) {
    return this.state.get(id) && this.state.get(id).toJS();
  }

  getIn(path) {
    const data = this.state.getIn(path);
    return isImmutable(data) ? data.toJS() : data;
  }

  checkSubscriptionsAndNotify(oldState, updatedState) {
    Object.entries(this.subscriptions).forEach(async (subscription) => {
      const path = subscription[0].split(',');
      const oldData = oldState.getIn(path);
      const newData = updatedState.getIn(path);

      if (!isEqual(oldData, newData)) {
        this.notify(path, newData);
      }
    });
  }

  set(id, obj) {
    const prevState = this.state;
    const updatedState = this.state.set(id, fromJS(obj));

    if (prevState.hashCode() !== updatedState.hashCode()) {
      this.state = this.state.set(id, fromJS(obj));
      this.checkSubscriptionsAndNotify(prevState, updatedState);
    }
  }

  setIn(path, obj) {
    const prevState = this.state;
    const updatedState = this.state.setIn(path, fromJS(obj));

    if (!prevState.equals(updatedState)) {
      this.state = updatedState;

      // notify all subscriptions
      this.checkSubscriptionsAndNotify(prevState, updatedState);
    }
  }

  remove(path) {
    this.state = this.state.deleteIn(path);
  }

  _createUnsubscribe(path, listener) {
    return () => {
      const listenerIndex = this.subscriptions[path]?.findIndex((subscription) => subscription.listener === listener);
      if (listenerIndex === -1) return;
      this.subscriptions[path].splice(listenerIndex, 1);
    };
  }

  async register(path, listener, type, coursewareId, coursewareChildId) {
    const pluginId = coursewareChildId || coursewareId;
    const subscription = { listener, coursewareId, pluginId };
    this.subscriptions[path] = this.subscriptions[path] || [];

    try {
      const subscriptionCount = this.subscriptions[path].reduce((acc, curr) => curr.pluginId === pluginId ? acc + 1 : acc, 0);
      const subscriptionLimit = 3;

      if (subscriptionCount > subscriptionLimit) {
        logNewrelicAddPageAction('subscriptionLimitExceeded', { pluginId: coursewareId, path });

        // eslint-disable-next-line no-console
        console.error(`Subscription exceeded limit of ${subscriptionLimit} for "${path}" with coursewareId ${pluginId}`);
      }

      this.subscriptions[path].push(subscription);

      const data = this.state.getIn(path);
      const dataJS = isImmutable(data) ? data.toJS() : data;

      if ((typeof dataJS !== 'undefined' && dataJS !== null) || type === VALUE_BIND_TYPE) {
        listener(dataJS);
      }

      return this._createUnsubscribe(path, listener);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }

    return () => {};
  }

  notify(path, updatedData) {
    if (this.subscriptions[path]) {
      const subscriptions = this.subscriptions[path];
      const data = isImmutable(updatedData) ? updatedData.toJS() : updatedData;

      subscriptions.forEach((subscription) => subscription.listener(data));
    }
  }
}

export default StateProvider;
